import { createContext } from "react";

import type { ListActionProgressState } from "containers/Resources/listActionProgress.utils";

/**
 * Bulk-action progress is keyed by the acting table's scope (its
 * `tableSettingsKey`) so a running bulk action lights up only the toolbar of the
 * table it was triggered from — not every `RecordTable` on the screen (e.g. the
 * role screen renders both a "Used by" and a "Permissions" table).
 *
 * Producers that do not supply a scope fall back to this global key, so their
 * progress shows in every table's toolbar (the legacy, unscoped behaviour) — an
 * unscoped producer therefore never becomes invisible.
 */
export const GLOBAL_LIST_ACTION_SCOPE = "__all__";

/**
 * The scope id (`tableSettingsKey`) of the table whose toolbar is currently
 * rendering. Provided by `RecordTableToolbar` and read by list-action producers
 * so they can tag the progress they dispatch with the acting table.
 *
 * NOTE: `tableSettingsKey` is a per-table-type constant, not a per-DOM-instance
 * id. That is sufficient here because the app never mounts two instances of the
 * same table type concurrently; if that invariant ever changes, a per-instance
 * suffix would be needed to disambiguate two simultaneous instances.
 */
export const ListActionScopeContext = createContext<string>("");

/**
 * Resolve which progress entry a table with `scopeId` should display: its own
 * scoped entry if present, otherwise any global (unscoped) entry, otherwise
 * nothing. Pure so it can be unit-tested without a store/render.
 */
export const selectProgressForScope = (
    progressMap: Record<string, ListActionProgressState> | null | undefined,
    scopeId: string
): ListActionProgressState | null =>
    progressMap?.[scopeId] ?? progressMap?.[GLOBAL_LIST_ACTION_SCOPE] ?? null;
